import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { PageHeader } from '~/components/ui';
import { useT, type Messages } from '~/i18n';
import { useOnboardingGate } from '~/components/shell/useOnboardingGate';
import { useEnglishAssessments } from './lib/useEnglishAssessments';

/**
 * Perfil: las tres caras de "qué sabemos de ti".
 *
 * Eran tres destinos sueltos en la barra —Hoja de vida, Preferencias, y la
 * bolsa de talento escondida como la última tarjeta de Preferencias— compitiendo
 * con Panel y Mensajes, que es a donde el candidato de verdad va todos los días.
 * Acá son tabs de una sola sección, y la bolsa de talento sube de tarjeta
 * enterrada a cara propia: es un CONSENTIMIENTO, la decisión más pesada que
 * toma en esta app, y estaba al fondo de un formulario largo donde nadie
 * scrollea.
 *
 * Cada tab es una RUTA y no un estado local: así el correo que le pide subir el
 * CV puede enlazar `/perfil/hoja-de-vida` directo, y el botón "atrás" del
 * navegador hace lo que la persona espera.
 */
type ProfileSection = keyof Messages['onboarding']['profile']['tabs'];

const TABS: Array<{ to: string; section: ProfileSection }> = [
  { to: '/perfil/hoja-de-vida', section: 'cv' },
  { to: '/perfil/preferencias', section: 'preferences' },
  { to: '/perfil/bolsa-de-talento', section: 'talentPool' },
  { to: '/perfil/plan', section: 'plan' },
  // El Programa de socios: la entrada visible para el candidato. La misma
  // pantalla vive suelta en /socios (la que se comparte y a la que vuelve Stripe).
  { to: '/perfil/socios', section: 'partners' },
];

/** Inglés aparece solo si una empresa le pidió alguna vez la evaluación. */
const ENGLISH_TAB: { to: string; section: ProfileSection } = { to: '/perfil/ingles', section: 'english' };

export default function JobApplyProfileLayout() {
  const t = useT('onboarding');
  const { pathname } = useLocation();
  const english = useEnglishAssessments();
  // «Quién te encuentra» solo con free_tier; apagado, la tab conserva su nombre.
  const { freeTier, publicProfileUrl } = useOnboardingGate();
  const showEnglish = pathname === ENGLISH_TAB.to || (english.data?.assessments.length ?? 0) > 0;
  const tabs = showEnglish ? [...TABS.slice(0, 3), ENGLISH_TAB, ...TABS.slice(3)] : TABS;
  /** La frase de la pantalla la pone la tab activa: el título es el mismo siempre. */
  const active = tabs.find((tab) => tab.to === pathname);
  /**
   * Ver la hoja pública desde cualquier cara del perfil (pedido de Clovis,
   * 2026-10-31): antes vivía solo en «Quién te encuentra». Privada, se ofrece
   * activarla allá (salvo que ya esté ahí).
   */
  const publicAction = publicProfileUrl ? (
    <a className="btn btn-outline btn-sm" href={publicProfileUrl} target="_blank" rel="noopener">
      {t.profile.viewPublicProfile}
    </a>
  ) : freeTier && pathname !== '/perfil/bolsa-de-talento' ? (
    <Link className="btn btn-ghost btn-sm" to="/perfil/bolsa-de-talento">
      {t.profile.enablePublicProfile}
    </Link>
  ) : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        scope="TBit Jobs"
        title={t.profile.title}
        lede={active ? t.profile.ledes[active.section] : undefined}
        actions={publicAction}
      />

      {/* role="tablist" NO: son enlaces que cambian la URL, y anunciarlos como
          tabs le promete al lector de pantalla flechas que no existen. */}
      <nav className="subnav" aria-label={t.profile.navLabel}>
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} className="subnav__link">
            {tab.section === 'talentPool' && !freeTier ? t.profile.talentPoolTabLegacy : t.profile.tabs[tab.section]}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
